import {
  Component,
  QueryList,
} from '@angular/core';

import * as d3 from 'd3';

import { ModalService } from '../../shared/modules/modal';
import { FlowChartItemComponent } from '../flow-chart/flow-chart.component';
import { NumberFormatService, SessionService } from '../../shared/services';
import { TransactionFlowChartConstants } from '../../shared/constants';
import {
  FlowChartLink,
  FlowChartNode,
  FlowChartGraph,
  FlowChartNodeWithCoordinates,
  CurrencyScale
} from '../../shared/models';

@Component({
  selector: 'flow-chart-modal',
  template: '',
})
export class FlowChartBaseComponent {
  isModal = false;
  items: QueryList<FlowChartItemComponent>;
  elementWidth: number;
  elementHeight: number;
  links: FlowChartLink[] = [];
  itemNodes: FlowChartNode[] = [];
  itemNodesById: FlowChartNodeWithCoordinates[] = [];
  upeCurrencyScale: number;

  elementSelector = TransactionFlowChartConstants.ELEMENT_SELECTOR;
  linkLineStroke = TransactionFlowChartConstants.LINK_LINE_STROKE;
  markerRef = TransactionFlowChartConstants.MARKER_REF;

  constructor(
    protected modalService: ModalService,
    protected numberFormatService: NumberFormatService,
    protected sessionService: SessionService
  ) { }

  doItems(): void {
    this.upeCurrencyScale = this.sessionService.getContainerUpeCurrencyScale();
    this.itemNodes = this.items.map(({ id, label, links, code, jurisdictionId }) => ({
      id,
      label,
      links,
      code,
      jurisdictionId,
    }));

    this.itemNodesById = <FlowChartNodeWithCoordinates[]> this.itemNodes.reduce((acc, item) => ({
      ...acc,
      [item.id]: item,
    }), {});

    this.itemNodes.forEach(({ links, id }) => {
      links.forEach(({ target, value }) => {
        this.links.push({
          value,
          source: this.itemNodesById[id],
          target: this.itemNodesById[target],
        });
      });
    });
  }

  calculate(): void {
    this.setWidthAndHeight();

    // I leave it here as my fix is very ugly and we need to solve this issue
    // const color = d3.scaleLinear<d3.RGBColor>()
    //   .domain([1, this.itemNodes.length])
    //   .range([rgb('#7559ae'), rgb('#ca3c92')])
    //   .interpolate(d3.interpolateHcl);

    const groupedNodesByJurisdictionId = {};
    for (let index = 0; index < this.itemNodes.length; index++) {
      const currentItemNode = this.itemNodes[index] as any;
      if (currentItemNode && currentItemNode.jurisdictionId !== null && currentItemNode.jurisdictionId !== undefined) {
        const itemNodeJurisdictionId = currentItemNode.jurisdictionId;
        groupedNodesByJurisdictionId[itemNodeJurisdictionId] ? groupedNodesByJurisdictionId[itemNodeJurisdictionId].push(currentItemNode) : groupedNodesByJurisdictionId[itemNodeJurisdictionId] = [currentItemNode];
      }
    }

    let colorIndex = 0;
    Object.values(groupedNodesByJurisdictionId).forEach((group: any) => {
      colorIndex = colorIndex + 1;
      group.forEach((node) => {
        // node.fill = color(colorIndex);
        node.fill = this.getInterpolatedColorCssRGB(colorIndex, this.itemNodes.length);
      });
    });
  }

  setWidthAndHeight(): void {
    this.elementWidth = TransactionFlowChartConstants.SVG_WIDTH;
    this.elementHeight = TransactionFlowChartConstants.SVG_HEIGHT;
  }

  initiate(): void {
    const svg = d3.select(this.elementSelector);
    const simulation = d3.forceSimulation()
      .force('link', d3.forceLink().distance(100).id((node: any) => node.id))
      .force('charge', d3.forceManyBody())
      .force('center', d3.forceCenter(this.elementWidth / 2, this.elementHeight / 2));

    const nodes: FlowChartNode[] = [];
    const links: FlowChartLink[] = [];

    this.itemNodes.forEach((node) => {
      nodes.push(<FlowChartNode>node);
    });

    this.links.forEach((link) => {
      const linkIndex = links.findIndex(comparedLink => {
        return (link.source.id === comparedLink.source.id && link.target.id === comparedLink.target.id) ||
          link.target.id === comparedLink.source.id && link.source.id === comparedLink.target.id;
      });

      // don't show decimals if scale is greater than Thousands, otherwise show 6 decimals
      const numberOfDecimals = this.upeCurrencyScale > CurrencyScale.Thousandths ? 6 : 0;
      link.value = this.numberFormatService.getFormatedNumber(
        this.numberFormatService.removeCommaAndGetNumber(link.value, 0),
        numberOfDecimals,
        numberOfDecimals
      );

      if (linkIndex > -1) {
        links[linkIndex].isTwoWay = links[linkIndex].target.id === link.source.id ||
          links[linkIndex].source.id === link.target.id;

        links[linkIndex].value = this.numberFormatService.getFormatedNumber(
          this.numberFormatService.removeCommaAndGetNumber(links[linkIndex].value, 0) +
          this.numberFormatService.removeCommaAndGetNumber(link.value, 0),
          numberOfDecimals,
          numberOfDecimals
        );
      } else {
        links.push(<FlowChartLink>link);
      }
    });

    const graph: FlowChartGraph = <FlowChartGraph>{ nodes, links };
    const transactionValues = {};
    nodes.forEach(({ id }) => transactionValues[id] = 0);

    const link = svg.append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(graph.links)
      .enter()
      .append('g')
      .attr('class', 'link')
      .append('line')
      .attr('stroke-width', this.linkLineStroke)
      .attr('stroke', 'lightgray')
      .attr('marker-end', `url(#${this.markerRef})`)
      .attr('marker-start', flowChartLink => flowChartLink.isTwoWay ? `url(#start-line)` : '');

    const node = svg.append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(graph.nodes)
      .enter()
      .append('g')
      .attr('class', 'draggable');

    node.append('circle')
      .attr('r', 12)
      .attr('fill', flowChartNode => flowChartNode.fill);

    node.append('text')
      .text(({ code }) => code)
      .attr('text-anchor', 'middle')
      .attr('y', -20)
      .attr('style', 'font-size: 12px');

    const linkText = svg.selectAll('.link')
      .append('text')
      .attr('class', 'link-label')
      .attr('fill', `${this.isModal ? 'black' : 'transparent'}`)
      .style('font-size', '5px')
      .attr('dy', '.35em')
      .attr('x', '4')
      .attr('text-anchor', 'middle')
      .text(({ value }) => value);

    node.append('title')
      .text(flowChartNode => flowChartNode.label);

    const ticked = () => {
      link.attr('x1', flowChartLink => flowChartLink.source.x)
        .attr('y1', flowChartLink => flowChartLink.source.y)
        .attr('x2', flowChartLink => flowChartLink.target.x)
        .attr('y2', flowChartLink => flowChartLink.target.y);

      linkText.attr('x', (flowChartLink: FlowChartLink) => ((flowChartLink.source.x + flowChartLink.target.x) / 2))
        .attr('y', (flowChartLink: FlowChartLink) => ((flowChartLink.source.y + flowChartLink.target.y) / 2));

      node.attr('transform', ({ x, y }) => 'translate(' + x + ',' + y + ')');
    };

    const onDragStart = (context) => {
      if (!d3.event.active) {
        simulation.alphaTarget(0.3).restart();
      }

      context.fx = context.x;
      context.fy = context.y;
    };

    const onDrag = (context) => {
      context.fx = d3.event.x;
      context.fy = d3.event.y;
    };

    const onDragEnd = (context) => {
      if (!d3.event.active) {
        simulation.alphaTarget(0);
      }

      context.fx = null;
      context.fy = null;
    };

    svg.selectAll('.draggable').call(d3.drag()
      .on('start', onDragStart)
      .on('drag', onDrag)
      .on('end', onDragEnd)
    );

    simulation
      .nodes(graph.nodes)
      .on('tick', ticked);

    simulation.force<d3.ForceLink<any, any>>('link')
      .links(graph.links);
  }

  getInterpolatedColorCssRGB(point, numPoints): string {
    const firstColorRGB = [117, 89, 174]; // hardcoded colors, match the original ones
    const secondColorRGB = [202, 60, 146];
    const changePercentage = (point - 1) / (numPoints - 1);
    let resRGB = [];

    for (let i = 0; i < 3; i++) {
      resRGB.push(Math.round(firstColorRGB[i] + (secondColorRGB[i] - firstColorRGB[i]) * changePercentage));
    }

    return `rgb(${resRGB[0]},${resRGB[1]},${resRGB[2]})`;
  }
}
